import { all, call, put, takeLatest } from 'redux-saga/effects'
import agent, { mocked } from 'agent'

import appMode from 'helpers/getAppMode'
import { get, hasPropertyChain, processFixesData } from 'helpers/util'

import * as actions from 'redux/actions/websiteAudit.actions'
import { AuditRestart } from '_base/Processing'

export function* getWebsiteAudit(domainId) {
  const websiteAudit = yield call(
    appMode.share
      ? agent.Share.WebsiteAudit.overview
      : appMode.preview
      ? mocked.Dashboard.websiteAudit
      : agent.Dashboard.websiteAudit,
    domainId
  )
  const fixes = []
  const score = []

  websiteAudit.data.forEach((item, index) => {
    const scoreValue = get([item, 'attributes', 'score'], '0')
    fixes.push({
      index,
      date: item.attributes.date,
      score: scoreValue,
      importantFixes: get([item, 'attributes', 'importantFixes'], 0),
      semiImportantFixes: get([item, 'attributes', 'semiImportantFixes'], 0)
    })
    score.push({
      index,
      date: item.attributes.date,
      score: scoreValue
    })
  })

  return {
    fixes: {
      chart: fixes,
      importantFixes: processFixesData(fixes, 'importantFixes'),
      semiImportantFixes: processFixesData(fixes, 'semiImportantFixes'),
      score: processFixesData(fixes, 'score')
    },
    score
  }
}

function* getAuditReportSaga(params) {
  try {
    const { domainId, auditId } = params.payload

    const body = yield call(
      appMode.share
        ? agent.Share.WebsiteAudit.get
        : appMode.preview
        ? mocked.WebsiteAudit.get
        : agent.WebsiteAudit.get,
      domainId,
      auditId
    )
    yield put(actions.getAuditReportSuccess(body.data, domainId))
  } catch (error) {
    yield put(actions.getAuditReportFailure(error))
  }
}

function* getAuditCrawlDataSaga(params) {
  try {
    const { domainId, auditId } = params.payload
    
    const body = yield call(
      appMode.share
        ? agent.Share.WebsiteAudit.getCrawlData
        : appMode.preview
        ? mocked.WebsiteAudit.getCrawlData
        : agent.WebsiteAudit.getCrawlData,
      domainId,
      auditId
    )
    yield put(actions.getAuditCrawlDataSuccess(body.data))
  } catch (error) {
    yield put(actions.getAuditCrawlDataFailure(error))
  }
}

function* getCompareAuditReportSaga(params) {
  try {
    const { domainId, auditId } = params.payload
    
    const body = yield call(
      appMode.share ? agent.Share.WebsiteAudit.get : agent.WebsiteAudit.get,
      domainId,
      auditId
    )
    yield put(actions.getCompareAuditReportSuccess(body.data))
  } catch (error) {
    yield put(actions.getCompareAuditReportFailure(error))
  }
}

function* getAllAuditReportsSaga(params) {
  try {
    const { domainId } = params.payload
    
    const [auditsStatus, fixesList] = yield all([
      call(
        appMode.share
          ? agent.Share.WebsiteAudit.getAll
          : appMode.preview
          ? mocked.WebsiteAudit.getAll
          : agent.WebsiteAudit.getAll,
        domainId
      ),
      call(getWebsiteAudit, domainId)
    ])
    const data = auditsStatus.data

    yield put(
      actions.getAllAuditReportsSuccess({
        audits: data,
        fixesList
      })
    )

    // Now get the report data for latest audit
    if (data.length > 0 && hasPropertyChain(data[0], 'id')) {
      const auditId = data[0].id
      yield put(actions.getAuditReport(domainId, auditId))
      yield put(actions.getAuditCrawlData(domainId, auditId))
    }

    // Load second report if available
    if (data.length > 1 && hasPropertyChain(data[1], 'id')) {
      const secondAuditId = data[1].id
      yield put(actions.getCompareAuditReport(domainId, secondAuditId))
    }
  } catch (error) {
    yield put(actions.getAllAuditReportsFailure(error))
  }
}

function* getDomainWebsiteAuditData(domain) {
  try {
    const domainId = domain.id
    
    const [websiteAudit, latestReport] = yield all([
      call(getWebsiteAudit, domainId),
      call(appMode.share ? agent.Share.WebsiteAudit.getAll : agent.WebsiteAudit.getLatest, domainId)
    ])

    return {
      ...domain,
      attributes: {
        ...domain.attributes,
        auditId: latestReport.data.id,
        overview: {
          ...latestReport.data.attributes.overview
        },
        status: latestReport.data.attributes.status,
        websiteAudit
      }
    }
  } catch (error) {
    yield put(actions.loadDomainsFailure(error))
    return {
      ...domain
    }
  }
}

function* loadDomainsSaga() {
  try {
    const body = yield call(agent.Domains.all)
    const editedDomains = yield all(
      body.data.map(domain => call(getDomainWebsiteAuditData, domain))
    )
    yield put(actions.loadDomainsSuccess(editedDomains))
  } catch (error) {
    yield put(actions.loadDomainsFailure(error))
  }
}

function* exportAuditReportSaga(params) {
  try {
    const { domainId, auditId } = params.payload
    const body = yield call(agent.WebsiteAudit.export, domainId, auditId)
    yield put(actions.exportAuditReportSuccess(body.data))
  } catch (error) {
    yield put(actions.exportAuditReportFailure(error))
  }
}

function* generateSitemapXMLSaga(params) {
  try {
    const { domainId } = params.payload
    const body = yield call(agent.WebsiteAudit.generateSitemap, domainId)
    yield put(actions.generateSitemapXMLSuccess(body.data))
  } catch (error) {
    yield put(actions.generateSitemapXMLFailure(error))
  }
}

function* restartAuditSaga({payload}) {
  try {
    const { domainId } = payload
    yield call(agent.WebsiteAudit.restartAudit, domainId)
    yield call(AuditRestart)
  } catch (error) {
    yield put(actions.restartAuditFailure(error))
  }
}

export default function* websiteAuditSagas() {
  yield all([
    takeLatest(actions.loadDomains.type, loadDomainsSaga),
    takeLatest(actions.getAuditReport.type, getAuditReportSaga),
    takeLatest(actions.getAuditCrawlData.type, getAuditCrawlDataSaga),
    takeLatest(actions.getCompareAuditReport.type, getCompareAuditReportSaga),
    takeLatest(actions.getAllAuditReports.type, getAllAuditReportsSaga),
    takeLatest(actions.exportAuditReport.type, exportAuditReportSaga),
    takeLatest(actions.generateSitemapXML.type, generateSitemapXMLSaga),
    takeLatest(actions.restartAudit.type, restartAuditSaga)
  ])
}
